/**
 * Persistence seam, keyed by journey type so parallel journeys coexist; also the
 * injection point for tests. A server-backed impl would be async, a deliberate
 * later change, not a drop-in behind this synchronous interface.
 */
export interface JourneyStorage {
  read(key: string): string | null;
  write(key: string, value: string): void;
  clear(key: string): void;
  /** Changes this client did not make (another tab today, a server push later). Optional. */
  subscribe?(key: string, listener: () => void): () => void;
}

const STORAGE_PREFIX = 'waipu.journey.';

const noop = () => undefined;

/**
 * localStorage-backed storage. Every access is guarded: private mode, disabled
 * storage, or a non-DOM environment must degrade to "no context", never throw.
 */
export const localJourneyStorage: JourneyStorage = {
  read(key) {
    try {
      return localStorage.getItem(STORAGE_PREFIX + key);
    } catch {
      return null;
    }
  },
  write(key, value) {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, value);
    } catch {
      // ignore: a lost context degrades to none, never breaks the flow
    }
  },
  clear(key) {
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch {
      // ignore
    }
  },
  // Fires only in the *other* tabs, which is the gap the client's own listeners leave. A null key
  // means storage was cleared wholesale.
  subscribe(key, listener) {
    // No DOM, no cross-tab channel. Degrades like every other access here.
    if (typeof window === 'undefined') {
      return noop;
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === STORAGE_PREFIX + key) {
        listener();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  },
};
